import {
  Box,
  Button,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import updateTuningHistoryNote from 'features/tuningHistory/api/updateTuningHistoryNote';
import useDebouncedValue from 'hooks/useDebouncedValue';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import PidSnapshotDisplay, { pidShape } from './pidSnapshotDisplay';
import TuningHistoryGraph from './tuningHistoryGraph';

const getTimestampDate = (timestamp) => {
  if (typeof timestamp?.toDate === 'function') {
    return timestamp.toDate();
  }

  return timestamp ? new Date(timestamp) : null;
};

export default function TuningHistory({
  history,
  pressId,
  averages,
  onApply,
  onApplySide,
}) {
  const averageEntry = averages.upper && averages.lower
    ? {
      upper: averages.upper,
      lower: averages.lower,
    }
    : null;

  return (
    <ExpandableSection
      headerText="Tuned PID History"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="m">
        <Container
          header={(
            <Header
              variant="h3"
              actions={(
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    disabled={!averages.upper}
                    onClick={() => onApplySide('upper', averages.upper)}
                  >
                    Apply Upper
                  </Button>
                  <Button
                    disabled={!averages.lower}
                    onClick={() => onApplySide('lower', averages.lower)}
                  >
                    Apply Lower
                  </Button>
                  <Button
                    disabled={!averageEntry}
                    onClick={() => onApply(averageEntry)}
                  >
                    Apply Average
                  </Button>
                </SpaceBetween>
                )}
            >
              Average PID
            </Header>
            )}
        >
          <PidSnapshotDisplay entry={averageEntry} showMaxOvershoot={false} />
        </Container>

        {history.length === 0 ? (
          <Box color="text-body-secondary">No tuned PID sets captured yet.</Box>
        ) : (
          <SpaceBetween size="m">
            {history.map((entry) => (
              <TuningHistoryEntry
                key={entry.id}
                entry={entry}
                pressId={pressId}
                onApply={onApply}
                onApplySide={onApplySide}
              />
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
}
function NoteEditor({ entryId, initialNote = '' }) {
  const [note, setNote] = useState(initialNote || '');
  const [saveStatus, setSaveStatus] = useState(null);
  const debouncedNote = useDebouncedValue(note, 800);
  const lastSavedNote = useRef(initialNote || '');

  useEffect(() => {
    if (debouncedNote === lastSavedNote.current) return;
    lastSavedNote.current = debouncedNote;
    updateTuningHistoryNote(entryId, debouncedNote)
      .then(() => setSaveStatus('saved'))
      .catch(() => setSaveStatus('error'));
  }, [debouncedNote, entryId]);

  return (
    <SpaceBetween size="xs">
      <Textarea
        value={note}
        onChange={({ detail }) => {
          setNote(detail.value);
          if (detail.value !== lastSavedNote.current) setSaveStatus('saving');
        }}
        placeholder="Add a note…"
        rows={2}
      />
      {saveStatus === 'saving' && (
        <Box color="text-body-secondary" fontSize="body-s">Saving…</Box>
      )}
      {saveStatus === 'saved' && (
        <Box color="text-status-success" fontSize="body-s">Saved</Box>
      )}
      {saveStatus === 'error' && (
        <Box color="text-status-error" fontSize="body-s">Failed to save</Box>
      )}
    </SpaceBetween>
  );
}

NoteEditor.propTypes = {
  entryId: PropTypes.string.isRequired,
  initialNote: PropTypes.string,
};

function TuningHistoryEntry({
  entry,
  pressId,
  onApply,
  onApplySide,
}) {
  const [showGraph, setShowGraph] = useState(false);
  const startDate = getTimestampDate(entry.startTime);
  const endDate = getTimestampDate(entry.endTime);
  const canShowGraph = Boolean(pressId && startDate && endDate && endDate > startDate);

  return (
    <Container
      header={(
        <Header
          variant="h3"
          actions={(
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => onApplySide('upper', entry.upper)}>
                Apply Upper
              </Button>
              <Button onClick={() => onApplySide('lower', entry.lower)}>
                Apply Lower
              </Button>
              <Button onClick={() => onApply(entry)}>
                Apply
              </Button>
            </SpaceBetween>
          )}
        >
          {`${startDate?.toLocaleString() || '--'} - ${endDate?.toLocaleTimeString() || '--'}`}
        </Header>
      )}
    >
      <SpaceBetween size="m">
        <PidSnapshotDisplay entry={entry} />
        <NoteEditor entryId={entry.id} initialNote={entry.note} />
        {canShowGraph && (
          <div>
            <Button onClick={() => setShowGraph((current) => !current)}>
              {showGraph ? 'Hide Graph' : 'Show Graph'}
            </Button>
          </div>
        )}
        {showGraph && canShowGraph && (
          <TuningHistoryGraph
            startTime={entry.startTime}
            endTime={entry.endTime}
            pressId={pressId}
          />
        )}
      </SpaceBetween>
    </Container>
  );
}

TuningHistoryEntry.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    note: PropTypes.string,
    startTime: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        toDate: PropTypes.func.isRequired,
      }),
    ]),
    endTime: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        toDate: PropTypes.func.isRequired,
      }),
    ]),
    upper: pidShape.isRequired,
    lower: pidShape.isRequired,
  }).isRequired,
  pressId: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onApplySide: PropTypes.func.isRequired,
};

TuningHistory.propTypes = {
  history: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    note: PropTypes.string,
    startTime: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        toDate: PropTypes.func.isRequired,
      }),
    ]),
    endTime: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        toDate: PropTypes.func.isRequired,
      }),
    ]),
    upper: pidShape.isRequired,
    lower: pidShape.isRequired,
  })).isRequired,
  pressId: PropTypes.string.isRequired,
  averages: PropTypes.shape({
    upper: pidShape,
    lower: pidShape,
  }).isRequired,
  onApply: PropTypes.func.isRequired,
  onApplySide: PropTypes.func.isRequired,
};
